"use client";

import { useEffect, useRef, useState } from "react";
import { useLocale, useTranslations } from "next-intl";

import { Link, usePathname, useRouter } from "../i18n/navigation";
import { useAuth } from "../lib/auth";

type NavItem = { href: string; labelKey: string };
type NavGroup = {
  labelKey: string;
  href?: string;
  items?: NavItem[];
};
// 对齐竞品导航：产品 / 解决方案 下拉，定价 直达。开发者中心本期不挂。
// 标签统一走翻译字典（common.*），避免在组件内硬编码。
const NAV_GROUPS: NavGroup[] = [
  {
    labelKey: "navProduct",
    items: [
      { href: "/create", labelKey: "navVideoTranslate" },
      { href: "/create", labelKey: "navMultilingualDubbing" },
      { href: "/voices", labelKey: "navVoiceClone" },
      { href: "/voices", labelKey: "navMyVoices" },
      { href: "/history", labelKey: "navMyWorks" },
    ],
  },
  {
    labelKey: "navSolutions",
    items: [
      { href: "/#scenes", labelKey: "navCrossBorder" },
      { href: "/#scenes", labelKey: "navShortDrama" },
      { href: "/#scenes", labelKey: "navVideoCreator" },
      { href: "/#scenes", labelKey: "navEducation" },
    ],
  },
  { labelKey: "navBlog", href: "/blog" },
  { labelKey: "navPricing", href: "/pricing" },
];

export default function SiteHeader() {
  const t = useTranslations("common");
  const locale = useLocale();
  const router = useRouter();
  const pathname = usePathname();
  const { isLoggedIn, authEmail, user, openLogin, logout } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const [openGroup, setOpenGroup] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    }
    if (menuOpen) document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [menuOpen]);

  // 路由变化后统一收起导航下拉与用户菜单，避免点击菜单项跳转后面板残留。
  useEffect(() => {
    setOpenGroup(null);
    setMenuOpen(false);
  }, [pathname]);

  // Esc 关闭导航下拉
  useEffect(() => {
    if (!openGroup) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpenGroup(null);
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [openGroup]);

  const isActive = (href: string) =>
    href === "/" ? pathname === "/" : pathname.startsWith(href);

  // 切换语言：替换路径首段 locale，保留其余路径；查询参数从当前 URL 读取后透传。
  // 不读取 window 之外的钩子（避免 useSearchParams 触发 Suspense 边界要求）。
  function switchLocale(next: string) {
    if (next === locale) return;
    const params = new URLSearchParams(window.location.search);
    const query = Object.fromEntries(params.entries());
    router.replace(
      { pathname: pathname || "/", query: Object.keys(query).length ? query : undefined },
      { locale: next },
    );
  }

  return (
    <nav className="cgNav">
      <div className="cgNavInner">
        <Link className="cgBrand" href="/">
          <div className="cgMark" aria-hidden="true">
            <span>C</span>
          </div>
          <div className="cgBrandText">
            <div className="cgBrandName">ClipGlobe</div>
            <div className="cgBrandTag">Multilingual Video Studio</div>
          </div>
        </Link>

        <div className="cgNavActions">
          <nav className="cgNavLinks" aria-label={t("navLabel")}>
            {NAV_GROUPS.map((g) =>
              g.items ? (
                <div
                  className={`cgNavDropdown${openGroup === g.labelKey ? " isOpen" : ""}`}
                  key={g.labelKey}
                  onMouseEnter={() => setOpenGroup(g.labelKey)}
                  onMouseLeave={() => setOpenGroup((cur) => (cur === g.labelKey ? null : cur))}
                >
                  <button
                    className="cgNavLink cgNavDropdownTrigger"
                    type="button"
                    aria-haspopup="true"
                    aria-expanded={openGroup === g.labelKey}
                    onClick={() =>
                      setOpenGroup((cur) => (cur === g.labelKey ? null : g.labelKey))
                    }
                  >
                    {t(g.labelKey)}
                    <span className="cgCaret" aria-hidden="true">
                      ▾
                    </span>
                  </button>
                  <div className="cgNavDropdownPanel" role="menu">
                    {g.items.map((it, i) => (
                      <Link
                        key={`${it.href}-${i}`}
                        className="cgNavDropdownItem"
                        href={it.href}
                        role="menuitem"
                        onClick={() => setOpenGroup(null)}
                      >
                        {t(it.labelKey)}
                      </Link>
                    ))}
                  </div>
                </div>
              ) : (
                <Link
                  key={g.labelKey}
                  className={`cgNavLink${isActive(g.href!) ? " cgNavLinkActive" : ""}`}
                  href={g.href!}
                  aria-current={isActive(g.href!) ? "page" : undefined}
                >
                  {t(g.labelKey)}
                </Link>
              ),
            )}
          </nav>

          {/* 语言切换器：切换时替换路径首段 locale，保留其余路径与查询参数 */}
          <div className="cgLangSwitch" role="group" aria-label="Language">
            <button
              type="button"
              className={locale === "zh" ? "isActive" : ""}
              aria-pressed={locale === "zh"}
              onClick={() => switchLocale("zh")}
            >
              {t("langZh")}
            </button>
            <button
              type="button"
              className={locale === "en" ? "isActive" : ""}
              aria-pressed={locale === "en"}
              onClick={() => switchLocale("en")}
            >
              {t("langEn")}
            </button>
          </div>

          {isLoggedIn && user ? (
            <div className="cgUserCluster" ref={menuRef}>
              <Link
                className={`cgNavRemaining${
                  user.plan_remaining_minutes / Math.max(1, user.plan_quota_minutes) <= 0.2
                    ? " cgNavRemainingLow"
                    : ""
                }`}
                href="/billing"
                title={t("viewPlanQuota")}
              >
                {t("remainingMinutes", { minutes: user.plan_remaining_minutes })}
              </Link>
              <button
                className="cgUserPill"
                onClick={() => setMenuOpen((o) => !o)}
                aria-haspopup="menu"
                aria-expanded={menuOpen}
              >
                <div className="cgUserAvatar">{authEmail[0]?.toUpperCase()}</div>
              </button>
              {menuOpen ? (
                <div className="cgUserMenu" role="menu">
                  <div className="cgUserMenuHead">
                    <div className="cgUserEmail">{authEmail}</div>
                  </div>
                  <Link
                    className="cgUserMenuItem"
                    href="/billing"
                    role="menuitem"
                    onClick={() => setMenuOpen(false)}
                  >
                    {t("myPlan")}
                  </Link>
                  <button
                    className="cgUserMenuItem cgUserMenuLogout"
                    role="menuitem"
                    onClick={() => {
                      setMenuOpen(false);
                      logout();
                    }}
                  >
                    {t("logout")}
                  </button>
                </div>
              ) : null}
            </div>
          ) : (
            <button className="cgBtn cgBtnSmall cgBtnPrimary" onClick={openLogin}>
              {t("loginRegister")}
            </button>
          )}
        </div>
      </div>
    </nav>
  );
}
